import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useMemo, useEffect } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import styled from 'styled-components';
import { Small } from 'foundations-components/transitional/components/Small';
import { SELECT_QUERY_ROW_HEIGHT } from '../constants';
import { useBoardDispatch, useBoardSelector } from '../redux/reduxHooks';
import SelectAllCheckbox from './SelectAllCheckbox';
import { clearSelection, getIsQuerySelected, getSelectedCardIds, selectQuery, setCardsSelected } from '../redux/selectionSlice';
import { useConfigs } from '../../lib/config-context';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { MERCURY_LAYER } from 'foundations-components/transitional/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const BULK_ACTIONS_CONTAINER_HEIGHT = '44px';
const BulkActionsSection = styled.div.withConfig({
  displayName: "BulkActionsContainer__BulkActionsSection",
  componentId: "sc-1i9ldiu-0"
})(({
  useReskin,
  theme
}) => `
  background-color: ${theme.color['fill-surface-default']};
  border: ${useReskin ? 'none' : `1px solid ${theme.color['border-core-subtle']}`};
  margin-block-end: ${useReskin ? theme.space['300'] : theme.space['0']};
  z-index: ${MERCURY_LAYER};
`);
function BulkActionsContainerInner({
  allCardIds,
  BulkActionsComponent,
  clearAllSelections,
  hideQuerySelection,
  isCurrentPageSelected,
  isQuerySelected,
  selectEverything,
  selectedCardIds,
  totalCards
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      paddingInlineStart: "200",
      blockSize: BULK_ACTIONS_CONTAINER_HEIGHT,
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(Small, {
        paddingInlineEnd: "200",
        children: /*#__PURE__*/_jsx(SelectAllCheckbox, {
          cardIds: allCardIds
        })
      }), /*#__PURE__*/_jsx(Small, {
        paddingInlineEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-data-board.bulkActions.selectionCount",
          options: {
            count: isQuerySelected ? totalCards : selectedCardIds.size
          }
        })
      }), /*#__PURE__*/_jsx(BulkActionsComponent, {
        selection: selectedCardIds,
        querySelected: isQuerySelected,
        clearSelection: clearAllSelections
      })]
    }), isCurrentPageSelected && allCardIds.length < totalCards && /*#__PURE__*/_jsx(Flex, {
      backgroundColor: "fill-info-subtle",
      paddingInlineStart: "200",
      blockSize: `${SELECT_QUERY_ROW_HEIGHT}px`,
      alignItems: "center",
      children: /*#__PURE__*/_jsx(Small, {
        paddingInlineStart: "200",
        children: isQuerySelected ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            useGap: true,
            message: "customer-data-board.bulkActions.querySelected",
            options: {
              count: totalCards
            }
          }), /*#__PURE__*/_jsx(Button, {
            "data-test-id": "clear-selection-button",
            use: "link",
            onClick: clearAllSelections,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-data-board.bulkActions.clearSelection"
            })
          })]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            useGap: true,
            message: "customer-data-board.bulkActions.pageSelected",
            options: {
              count: allCardIds.length
            }
          }), !hideQuerySelection && /*#__PURE__*/_jsx(Button, {
            "data-test-id": "select-query-button",
            use: "link",
            onClick: selectEverything,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-data-board.bulkActions.selectQuery",
              options: {
                count: totalCards
              }
            })
          })]
        })
      })
    })]
  });
}
function BulkActionsContainer({
  allCardIds,
  BulkActionsComponent,
  isBulkActionsRedesignEnabled,
  totalCards
}) {
  const selectedCardIds = useBoardSelector(getSelectedCardIds);
  const isQuerySelected = useBoardSelector(getIsQuerySelected);
  const {
    hideQuerySelection,
    useReskin
  } = useConfigs();
  const dispatch = useBoardDispatch();
  const previousCardIds = usePrevious(allCardIds);
  useEffect(() => {
    if (JSON.stringify(previousCardIds) !== JSON.stringify(allCardIds)) {
      selectedCardIds.forEach(cardId => {
        if (!allCardIds.includes(cardId)) {
          dispatch(setCardsSelected({
            cards: [{
              cardId,
              selected: false
            }]
          }));
        }
      });
    }
  }, [allCardIds, selectedCardIds, dispatch, previousCardIds]);
  const clearAllSelections = useCallback(() => dispatch(clearSelection()), [dispatch]);
  const isCurrentPageSelected = useMemo(() => allCardIds.length > 0 && allCardIds.every(cardId => selectedCardIds.has(cardId)), [allCardIds, selectedCardIds]);
  const selectEverything = useCallback(() => dispatch(selectQuery()), [dispatch]);

  // If query is selected and user clicks show more then those cards wont be checked. This makes sure we track those when they load
  useEffect(() => {
    if (isQuerySelected && selectedCardIds.size !== allCardIds.length) {
      const unselectedCards = allCardIds.filter(cardId => !selectedCardIds.has(cardId));
      dispatch(setCardsSelected({
        cards: unselectedCards.map(cardId => ({
          cardId,
          selected: true
        })),
        querySelected: isQuerySelected
      }));
    }
  }, [allCardIds, selectedCardIds, isQuerySelected, dispatch]);

  // Always mount the component when the redesign path is enabled so that the
  // collapsible wrapper inside `BulkActionsRedesignWrapper` can animate.
  if (!BulkActionsComponent || !isBulkActionsRedesignEnabled && selectedCardIds.size === 0) {
    return null;
  }
  const hasMoreCardsToSelect = selectedCardIds.size < totalCards;
  const showSelectQuery = selectedCardIds.size > 0 && hasMoreCardsToSelect && !isQuerySelected && !hideQuerySelection;
  return isBulkActionsRedesignEnabled ? /*#__PURE__*/_jsx(BulkActionsComponent, {
    clearSelection: clearAllSelections,
    isBulkActionsRedesignEnabled: isBulkActionsRedesignEnabled,
    onClose: clearAllSelections,
    onSelectQuery: selectEverything,
    paddingBlockEnd: "300",
    querySelected: isQuerySelected,
    selection: selectedCardIds,
    selectionCount: isQuerySelected ? totalCards : selectedCardIds.size,
    showSelectQuery: showSelectQuery,
    totalCount: totalCards
  }) : /*#__PURE__*/_jsx(BulkActionsSection, {
    useReskin: Boolean(useReskin),
    children: /*#__PURE__*/_jsx(BulkActionsContainerInner, {
      allCardIds: allCardIds,
      BulkActionsComponent: BulkActionsComponent,
      clearAllSelections: clearAllSelections,
      hideQuerySelection: !!hideQuerySelection,
      isCurrentPageSelected: isCurrentPageSelected,
      isQuerySelected: isQuerySelected,
      selectEverything: selectEverything,
      selectedCardIds: selectedCardIds,
      totalCards: totalCards
    })
  });
}
export default BulkActionsContainer;